import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import loadable from "@loadable/component";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import { FileType, checkFileType } from "@app/helpers/check-file-type";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { LinkButtonAdapter } from "../link-button-adapter/LinkButtonAdapter";
import { Asset, Button, RichText } from "../../models";
import {
  BlockHeader,
  HeaderLevel,
} from "../../../common/block-header/BlockHeader";
import { BlockBody } from "../../../common/block-body/BlockBody";

const ReactPlayer = loadable(() => import("react-player"));

type Props = {
  title?: RichText;
  text?: RichText;
  button?: Button[];
  media?: Asset;
  isColorBackground?: boolean;
  isPlayOnClick?: boolean;
  isPlayOnce?: boolean;
  isSoundEnabled?: boolean;
  isShowControls?: boolean;
};

export const SimpleText: FC<Props> = ({
  title,
  text,
  button,
  media,
  isColorBackground,
  isPlayOnClick,
  isPlayOnce,
  isSoundEnabled,
  isShowControls,
}) => (
  <div
    className={classNames(
      "mt-20 flex flex-col items-center px-5",
      "lg:mt-25 lg:px-0",
      "xl:mt-40",
      { "bg-dark py-20": isColorBackground },
      { "lg:py-25": isColorBackground },
      { "xl:py-30": isColorBackground },
    )}
  >
    <div
      className={classNames(
        "text-center",
        "lg:max-w-[66%]",
        "xl:max-w-[980px]",
      )}
    >
      <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
        {title ? (
          <div
            className={classNames(
              "text-medium text-primary-content",
              "lg:text-large",
              "xl:text-xx-large",
              { "mb-5 lg:mb-8": text || button?.length },
            )}
          >
            <BlockHeader richText={title} headerLevel={HeaderLevel.H2} />
          </div>
        ) : null}
        {text ? (
          <div
            className={classNames(
              "text-regular text-base-400",
              "[&>p]:mb-2.5 last:[&>p]:mb-0 [&>p]:lg:mb-5",
              {
                "mb-5 lg:mb-8": button?.length,
              },
            )}
          >
            <BlockBody richText={text} />
          </div>
        ) : null}
        {button?.length ? <LinkButtonAdapter data={button[0]} /> : null}
      </Fade>
      {media?.filename ? (
        <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
          <div
            className={classNames(
              "mt-10 rounded-lg",
              "lg:mx-auto lg:max-w-[60%]",
              "xl:mt-15",
            )}
          >
            {checkFileType(media.filename) === FileType.VIDEO ? (
              <ReactPlayer
                url={media.filename}
                width="100%"
                height="auto"
                playing={!isPlayOnClick}
                muted={!isSoundEnabled}
                loop={!isPlayOnce}
                playsinline
                config={{
                  file: {
                    attributes: { disablePictureInPicture: true },
                  },
                }}
                controls={isShowControls}
              />
            ) : (
              <GatsbyImage
                image={getGatsbyImage(media.filename, {
                  layout: "fullWidth",
                })}
                alt={media.alt}
                className={classNames("w-full rounded-lg")}
              />
            )}
          </div>
        </Fade>
      ) : null}
    </div>
  </div>
);
